import { ContentMedia } from '../media/types';
import { Scenario } from '../userScenario/types';

export interface Topic {
  topicId: number;
  name: string;
  iconUrl: string;
}

export interface Tag {
  tagId: number;
  name: string;
  contentCount: number;
}

export interface TopicsResponse {
  topics: Topic[];
  tags?: Tag[];
}

export interface LanguagePairs {
  languagePairId: number;
  sourceLanguageLocale: string;
  targetLanguageLocale: string;
  targetLanguageImage: string;
  targetLanguageFlagImage: string;
  sourceLanguageName: string;
  targetLanguageName: string;
}

export interface UserLanguagesResponse {
  languagePairs: LanguagePairs[];
}

export interface UserLanguageInfoResponse {
  dateStarted: string;
}

export interface UserPathProgress {
  numberOfScenariosCompleted: number;
  numberOfItemsLearned: number;
}

export interface UserScenario {
  userScenarioId: number;
  scenarioId: number;
  isPremium: boolean;
  title: string;
  iconUrl: string;
  topicPhotoUrl: string;
  topicName: string;
  isStarted: boolean;
  numberOfLearnables: number;
  itemsLearned: number;
  progressPercent: number;
}

export interface PathReviewMode {
  counter: number | null;
  isEnabled: boolean;
}

export interface PathReviewModes {
  classicReview: PathReviewMode;
  speedReview: PathReviewMode;
  difficultWords: PathReviewMode;
}

export interface CurrentStatusResponse {
  pathName: string;
  progress: UserPathProgress;
  languagePair: {
    languagePairId: number;
    sourceLanguage: string;
    targetLanguage: string;
  };
  userScenario?: UserScenario;
  reviewModes: PathReviewModes;
  remainingPremiumScenarios: number;
}

export interface ReviewMode {
  count: number;
  enabled: boolean;
}

export interface PracticeResponse {
  completedScenariosCount: number;
  nothingLearnedYet: boolean;
  review: ReviewMode;
  difficultWords: ReviewMode;
}

export interface LanguagePairImmerseResponse {
  eligibleForImmerse: boolean;
  recentlyJoined: boolean;
  unwatchedVideosCount: number;
  watchedVideosCount: number;
  needsPracticeVideosCount: number;
  readyToWatchVideosCount: number;
  featuredContentUrl: string | null;
}

export interface LanguagePairProgressConversion {
  languagePairId: number;
  sourceLanguageName: string;
  targetLanguageName: string;
  numberOfScenariosMovedToProgress: number;
  numberOfScenariosMovedToCompleted: number;
}

export interface ProgressConversionResponse {
  updatedLanguagePairs: LanguagePairProgressConversion[];
  mostRecentlyLearnedLanguagePairId: number | null;
}

export interface ProgressResponse {
  learnablesFullyGrownCount: number;
  learnablesTouchedButNotFullyGrownCount: number;
  contentMediaWatched: number;
  contentMediaReplayed: number;
  missionsCompleted: number;
  missionsReplayed: number;
}

export interface UserPoints {
  totalPoints: number;
  learnablePoints: number;
  contentMediaPoints: number;
  missionPoints: number;
}

export interface UserLevel {
  currentLevel: number;
  minimumPoints: number;
  maximumPoints: number;
}

export interface UserStage {
  stageName: string;
  stageDescription: string;
  stageMinPoints: number;
  stageMaxPoints: number;
}

export interface PointsResponse extends Record<string, unknown> {
  points: UserPoints;
  level: UserLevel;
  stage: UserStage;
  lastUpdated: string;
}

export interface Communicate {
  locationSlug: string;
  title: string;
  category: string;
  photoUrl: string;
}

export type RecommendationsActivityType = 'learn' | 'immerse' | 'communicate';

export interface RecommendationsResponse extends Record<string, unknown> {
  learn: {
    nextScenario: Scenario;
    nextFreeScenario: Scenario;
  };
  immerse: ContentMedia;
  communicate: Communicate;
  recommendationOrdering: RecommendationsActivityType[];
}

export type ActivityType = 'learn' | 'difficultWordsReview' | 'review' | 'immerse' | 'communicate';

export interface StatsRequest {
  startDate: string;
  endDate: string;
}

export interface StatsResponse {
  wordsLearned: number;
  wordsReviewed: number;
  wordsHeard: number;
  videosWatched: number;
  videosUnderstood: number;
  partlyUnderstood: number;
  membotChats: number;
}

export const SUPPORTED_LEARNING_FEATURE_TYPES = [
  'learn',
  'video',
  'conversation',
  'review',
  'pronunciation',
  'sentence_builder',
] as const;

export type LearningFeatureType = (typeof SUPPORTED_LEARNING_FEATURE_TYPES)[number];

export interface LearningFeature {
  type: LearningFeatureType;
  skippable: boolean;
}

export interface LearningQueueResponse {
  lastUpdated: string;
  learningFeatures: LearningFeature[];
}
